import Rails from "@rails/ujs";
import * as ActiveStorage from "@rails/activestorage";
import { onPageLoad } from "./shared/page_load";
import { initializePlansPaymentModal } from "./shared/plans_payment";

Rails.start();
ActiveStorage.start();

function initializeTooltips() {
  if (!window.bootstrap || !window.bootstrap.Tooltip) {
    return;
  }

  document.querySelectorAll('[data-bs-toggle="tooltip"]').forEach((element) => {
    if (element.dataset.tooltipInitialized === "true") {
      return;
    }

    new window.bootstrap.Tooltip(element);
    element.dataset.tooltipInitialized = "true";
  });
}

function initializeTicketTotalDisplays() {
  document.querySelectorAll("[data-ticket-total-display]").forEach((display) => {
    const scope = display.closest("[data-ticket-total-form]") || display.closest("form") || document;
    const selects = scope.querySelectorAll(".ticket-select");
    const prices = scope.querySelectorAll(".ticket-price");

    if (selects.length === 0 || prices.length === 0) {
      return;
    }

    const updateTotal = () => {
      let total = 0;

      selects.forEach((select, index) => {
        const count = Number.parseInt(select.value, 10) || 0;
        const price = Number.parseInt(prices[index].value, 10) || 0;
        total += count * price;
      });

      display.textContent = total.toLocaleString();
    };

    if (display.dataset.ticketTotalInitialized === "true") {
      updateTotal();
      return;
    }

    selects.forEach((select) => {
      select.addEventListener("change", updateTotal);
    });

    updateTotal();
    display.dataset.ticketTotalInitialized = "true";
  });
}

function initializeCheckAllToggles() {
  document.querySelectorAll("[data-check-all-target]").forEach((toggle) => {
    if (toggle.dataset.checkAllInitialized === "true") {
      return;
    }

    const targetSelector = toggle.dataset.checkAllTarget;
    const getTargets = () => Array.from(document.querySelectorAll(targetSelector));
    const updateToggleState = () => {
      const targets = getTargets();

      if (targets.length === 0) {
        toggle.checked = false;
        return;
      }

      toggle.checked = targets.every((target) => target.checked);
    };

    toggle.addEventListener("change", () => {
      getTargets().forEach((target) => {
        target.checked = toggle.checked;
      });
    });

    getTargets().forEach((target) => {
      target.addEventListener("change", updateToggleState);
    });

    updateToggleState();
    toggle.dataset.checkAllInitialized = "true";
  });
}

function showCopyToast(button) {
  if (!window.bootstrap || !window.bootstrap.Toast) {
    return;
  }

  const toastSelector = button.dataset.copyToastTarget || "#copyToast";
  const toastElement = document.querySelector(toastSelector);

  if (!toastElement) {
    return;
  }

  const delay =
    Number.parseInt(button.dataset.copyToastDelay || toastElement.dataset.copyToastDelay, 10) ||
    1500;

  new window.bootstrap.Toast(toastElement, { delay }).show();
}

function initializeCopyButtons() {
  document.querySelectorAll("[data-copy-text]").forEach((button) => {
    if (button.dataset.copyInitialized === "true") {
      return;
    }

    button.addEventListener("click", () => {
      navigator.clipboard
        .writeText(button.dataset.copyText)
        .then(() => {
          showCopyToast(button);
        })
        .catch((error) => {
          console.error("コピーに失敗しました", error);
        });
    });

    button.dataset.copyInitialized = "true";
  });
}

function initializeVisibilityToggles() {
  document.querySelectorAll("[data-toggle-visibility-target]").forEach((toggle) => {
    if (toggle.dataset.visibilityToggleInitialized === "true") {
      return;
    }

    const target = document.querySelector(toggle.dataset.toggleVisibilityTarget);

    if (!target) {
      return;
    }

    const displayValue = toggle.dataset.toggleDisplayValue || "block";
    const updateVisibility = () => {
      target.style.display = toggle.checked ? displayValue : "none";
    };

    toggle.addEventListener("change", updateVisibility);
    updateVisibility();
    toggle.dataset.visibilityToggleInitialized = "true";
  });
}

function initializeTabActiveBars() {
  document.querySelectorAll('button[data-bs-toggle="tab"]').forEach((button) => {
    if (button.dataset.activeBarInitialized === "true" || !button.querySelector(".active-bar")) {
      return;
    }

    button.addEventListener("shown.bs.tab", (event) => {
      const navContainer = event.target.closest(".nav");

      if (!navContainer) {
        return;
      }

      navContainer.querySelectorAll(".active-bar").forEach((bar) => {
        bar.style.transform = "scaleX(0)";
        bar.style.opacity = "0";
      });

      navContainer.querySelectorAll('.nav-link[data-bs-toggle="tab"]').forEach((navLink) => {
        navLink.classList.add("text-muted");
        navLink.classList.remove("text-dark");
      });

      const activeBar = event.target.querySelector(".active-bar");
      if (activeBar) {
        activeBar.style.transform = "scaleX(1)";
        activeBar.style.opacity = "1";
      }

      event.target.classList.remove("text-muted");
      event.target.classList.add("text-dark");
    });

    button.dataset.activeBarInitialized = "true";
  });
}

function initializeBrowserActionButtons() {
  document.querySelectorAll("[data-browser-action]").forEach((button) => {
    if (button.dataset.browserActionInitialized === "true") {
      return;
    }

    button.addEventListener("click", () => {
      const fallbackUrl = button.dataset.browserFallbackUrl;

      switch (button.dataset.browserAction) {
        case "back":
          window.history.back();
          break;
        case "close":
          window.close();
          window.setTimeout(() => {
            if (window.closed) {
              return;
            }

            if (fallbackUrl) {
              window.location.assign(fallbackUrl);
              return;
            }

            if (document.referrer) {
              window.location.assign(document.referrer);
              return;
            }

            if (window.history.length > 1) {
              window.history.back();
            }
          }, 120);
          break;
        default:
          break;
      }
    });

    button.dataset.browserActionInitialized = "true";
  });
}

function initializeStageSelectToggleButtons() {
  document.querySelectorAll(".btn-check").forEach((input) => {
    if (input.dataset.stageToggleInitialized === "true") {
      return;
    }

    const label = document.querySelector(`label[for="${input.id}"].reception-stage-select__toggle`);

    if (!label) {
      return;
    }

    let pointerTriggered = false;

    label.addEventListener("pointerup", () => {
      pointerTriggered = true;
    });

    input.addEventListener("change", () => {
      if (!pointerTriggered) {
        return;
      }

      window.requestAnimationFrame(() => {
        input.blur();

        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
      });

      pointerTriggered = false;
    });

    input.dataset.stageToggleInitialized = "true";
  });
}

async function initializeQrScanner() {
  const video = document.querySelector("#video");

  if (!video || video.dataset.qrScannerInitialized === "true") {
    return;
  }

  video.dataset.qrScannerInitialized = "true";

  try {
    const { BrowserQRCodeReader } = await import(
      /* webpackChunkName: "qr-code-reader" */
      "@zxing/library/esm/browser/BrowserQRCodeReader"
    );

    const codeReader = new BrowserQRCodeReader();

    codeReader
      .decodeFromInputVideoDevice(undefined, "video")
      .then((result) => {
        codeReader.reset();
        window.location.assign(`${window.location.href}/${result.text}`);
      })
      .catch((error) => {
        console.error(error);
        video.dataset.qrScannerInitialized = "false";
      });
  } catch (error) {
    console.error("QRコードリーダーの初期化に失敗しました", error);
    video.dataset.qrScannerInitialized = "false";
  }
}

onPageLoad(() => {
  initializeTooltips();
  initializeTicketTotalDisplays();
  initializeCheckAllToggles();
  initializeCopyButtons();
  initializeVisibilityToggles();
  initializeTabActiveBars();
  initializeBrowserActionButtons();
  initializeStageSelectToggleButtons();
  initializePlansPaymentModal();
  initializeQrScanner();
});
